import i18n from "@/i18n";
import moment from "moment";
import { Activity } from "../api/activities";
import { Delivery } from "../api/deliveries";
import { Opportunity } from "../api/opportunities";
import { SupportCase } from "../api/supportCase";
import { UserProvider } from "../api/user";

import { Context } from "../contex";
import { DisplayType, EntityType, MetadataProvider } from "./metadata";
import { FieldName } from "./names";

export class PresentationHelper {

    static formatField(fieldName: string, entity: any, entityType?: EntityType) {
        const metadata = MetadataProvider.getFieldByName(fieldName);
        if (!metadata) return '';
        entityType ??= metadata.entityType;

        const friendlyName = MetadataProvider.getFriendlyName(fieldName, entityType);
        const value = entity[friendlyName];

        switch (metadata.type) {
            case DisplayType.C:
                return this.formatBoolean(value);
            case DisplayType.D:
            case DisplayType.DI:
            case DisplayType.SD:
            case DisplayType.SDI:
                return this.formatDropdown(value, fieldName);
            case DisplayType.I:
            case DisplayType.IC:
            case DisplayType.R:
                return this.formatNumberWithCurrency(value, this.getCurrency(fieldName, entity, entityType));
            case DisplayType.DA:
            case DisplayType.DAT:
                return this.formatDate(value, this.getAllDayShift(fieldName, entity));
            case DisplayType.DT:
            case DisplayType.DTGMT:
                return this.formatDateTime(value, this.getAllDayShift(fieldName, entity));
            case DisplayType.PI:
                return this.getPersonName(fieldName, entity, entityType);
            case DisplayType.UI:
                return this.formatUserName(value);
            default:
                return value?.toString() || '';
        }
    }

    static formatBoolean(value: any) {
        return ['', '0', 'false', 'no'].includes(value?.toString()?.toLowerCase() || '') ? i18n.tc('no') : i18n.tc('yes');
    }

    static formatDateTime(value: string, isAllDayShift = false, includeSeconds = false) {
        if (!value) return '';
        const result = [this.formatDate(value, isAllDayShift)];
        if (!moment.utc(value).isSame(moment.utc(value).startOf('day'))) {
            result.push(this.formatTime(value, includeSeconds));
        }
        return result.join(' ');
    }

    static formatDate(value: string, isAllDayShift = false) {
        if (!value) return '';
        if (value.toString().match(/^\d{8}$/)) {
            value = value.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
        }
        return moment.utc(value).subtract(isAllDayShift ? 1 : 0, 'day').local().format(Context.settings.dateFormat.toUpperCase());
    }

    static formatTime(value: string, includeSeconds = false) {
        if (!value) return '';
        let timeFormat = Context.settings.timeFormat.replace('tt', 'a');
        if (includeSeconds) {
            timeFormat = timeFormat.replace(':mm', ':mm:ss');
        }
        return moment.utc(value).local().format(timeFormat);
    }

    static formatDropdown(value: any, fieldName: string) {
        value = value?.toString();
        if (!value) return '';
        const metadata = MetadataProvider.getFieldByName(fieldName);
        if (!metadata || !metadata.options) return value;
        return metadata.options.find(op => op.value == value)?.label || value;
    }

    static formatNumberWithCurrency(value: number, currencyId: string) {
        const result = [this.formatNumber(value)];
        if (Context.settings.features.currency) {
            result.push(this.formatDropdown(currencyId, FieldName.UserCurrency));
        }
        return result.filter(v => v).join(' ');
    }

    static formatNumber(value: number, fractionDigits?: number) {
        var parts = value.toString().split('.');
        if (parts.length > 0) {
            parts[0] = parts[0].split('').reverse()
                .map((d, i) => d != '-' && i > 0 && i % 3 == 0 ? d + Context.settings.thousandSeparator : d)
                .reverse().join('')
        }
        if (parts.length > 1 && fractionDigits) {
            parts[1] = parts[1].slice(0, fractionDigits);
        }
        return parts.join(Context.settings.decimalSeparator);
    }

    static formatUserName(value: number) {
        if (!+value) return '';
        return UserProvider.getUserName(+value);
    }

    static getAllDayShift(fieldName: string, entity: any) {
        if (fieldName == FieldName.ActivityDateTime) {
            return (entity as Activity).ActivityAllDay;
        }
        return false;
    }

    static getCurrency(fieldName: string, entity: any, entityType: EntityType) {
        if (fieldName == FieldName.OpportunityOrderValue && entityType == EntityType.Opportunity) {
            return (entity as Opportunity).OpportunityCurrencyName;
        }
        if (fieldName == FieldName.OpportunityOrderValue && entityType == EntityType.Delivery) {
            return (entity as Delivery).DeliveryOpportunityCurrencyName;
        }
        return '';
    }

    static getPersonName(fieldName: string, entity: any, entityType: EntityType) {
        if (fieldName == FieldName.ActivityPersonId && entityType == EntityType.Activity) {
            return (entity as Activity).ActivityPersonName;
        }
        if (fieldName == FieldName.OpportunityPersonId && entityType == EntityType.Opportunity) {
            return (entity as Opportunity).OpportunityPersonName;
        }
        if (fieldName == FieldName.DeliveryPersonId && entityType == EntityType.Delivery) {
            return (entity as Delivery).DeliveryPersonName;
        }
        if (fieldName == FieldName.SupportCasePersonId && entityType == EntityType.SupportCase) {
            return (entity as SupportCase).SupportCasePersonName;
        }
        return '';
    }
}